import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, SectionHeader, Spinner, Modal } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVELS = ['A1', 'A2', 'B1', 'B2'];
const CATS   = ['lesson', 'exam_tips', 'vocabulary', 'grammar'];
const LABELS = { lesson: 'Lesson', exam_tips: 'Exam Tips', vocabulary: 'Vocabulary', grammar: 'Grammar' };

const emptyForm = { title: '', description: '', youtube_url: '', level: 'A1', category: 'lesson' };

// ── Level tabs (same pattern across all 4 pages) ──────────────
function LevelTabs({ active, onChange }) {
  return (
    <div className="flex gap-0 rounded-xl overflow-hidden border border-gray-200 w-fit shadow-sm flex-shrink-0">
      {LEVELS.map((lv, i) => (
        <button
          key={lv}
          onClick={() => onChange(lv)}
          className="relative px-5 py-2 text-sm font-bold transition-all duration-200 focus:outline-none"
          style={{
            background:  active === lv ? '#DD0000' : '#FFFFFF',
            color:       active === lv ? '#FFFFFF' : '#1A1A1A',
            borderRight: i < LEVELS.length - 1 ? '1px solid #E0E0E0' : 'none',
          }}
          onMouseEnter={e => { if (active !== lv) e.currentTarget.style.background = '#FFF0F0'; }}
          onMouseLeave={e => { if (active !== lv) e.currentTarget.style.background = '#FFFFFF'; }}
        >
          {active === lv && (
            <span
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ background: '#FFCE00' }}
            />
          )}
          {lv}
        </button>
      ))}
    </div>
  );
}

export default function AdminVideosPage() {
  const { addToast }  = useToast();
  const [videos, setVideos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [deleteModal, setDelete]  = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [preview, setPreview]     = useState(null); // extracted youtube_id
  const [activeLevel, setActiveLevel] = useState('A1');

  const load = () => {
    API.get('/videos')
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => addToast('Failed to load', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (k === 'youtube_url') {
      const m = v.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/
      );
      setPreview(m ? m[1] : null);
    }
    // When level changes in form, also switch the filter tab for convenience
    if (k === 'level') setActiveLevel(v);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.youtube_url.trim()) {
      addToast('Title and YouTube URL are required', 'error'); return;
    }
    if (!preview) { addToast('Invalid YouTube URL', 'error'); return; }
    setSaving(true);
    try {
      await API.post('/videos', form);
      addToast('Video added!', 'success');
      setForm(emptyForm);
      setPreview(null);
      load();
    } catch (err) { addToast(err.message || 'Failed to add video', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await API.delete(`/videos/${deleteModal.id}`);
      addToast('Video deleted', 'success');
      setVideos(p => p.filter(v => v.id !== deleteModal.id));
      setDelete(null);
    } catch (err) { addToast(err.message || 'Delete failed', 'error'); }
    finally { setDeleting(false); }
  };

  // Client-side filter by active level tab
  const filtered = videos.filter(v => v.level === activeLevel);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">

      {/* Add video form */}
      <Card className="p-6" accent="red">
        <SectionHeader title="Add Video" subtitle="Paste a YouTube URL to add a lesson video" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">

          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">
              YouTube URL *
            </label>
            <input
              type="url" value={form.youtube_url}
              onChange={e => setF('youtube_url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none font-mono"
            />
            {preview && (
              <div className="mt-2 flex items-center gap-3 p-2 bg-green-50 border border-green-200 rounded-xl">
                <img
                  src={`https://img.youtube.com/vi/${preview}/default.jpg`}
                  alt="thumb"
                  className="w-16 h-11 rounded object-cover border border-green-200"
                />
                <p className="text-xs text-green-700 font-semibold">
                  ✓ Valid YouTube video detected
                </p>
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">Title *</label>
            <input
              type="text" value={form.title}
              onChange={e => setF('title', e.target.value)}
              placeholder="e.g. A1 — German Greetings and Introductions"
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-de-black mb-1.5">
              Description
            </label>
            <textarea
              value={form.description} onChange={e => setF('description', e.target.value)}
              placeholder="What will students learn from this video?"
              rows={2}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Level *</label>
            <select
              value={form.level} onChange={e => setF('level', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white"
            >
              {LEVELS.map(l => <option key={l} value={l}>Level {l}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-de-black mb-1.5">Category</label>
            <select
              value={form.category} onChange={e => setF('category', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white"
            >
              {CATS.map(c => <option key={c} value={c}>{LABELS[c]}</option>)}
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button variant="primary" loading={saving} onClick={handleSave}>
            + Add Video
          </Button>
        </div>
      </Card>

      {/* Videos list with level filter tabs */}
      <Card className="overflow-hidden">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <SectionHeader title={`All Videos (${videos.length})`} />
          <LevelTabs active={activeLevel} onChange={setActiveLevel} />
        </div>

        {/* Level count summary */}
        <div
          className="px-5 py-2 border-b border-gray-100 flex gap-4 flex-wrap"
          style={{ background: '#F9F9F9' }}
        >
          {LEVELS.map(lv => {
            const count = videos.filter(v => v.level === lv).length;
            return (
              <button
                key={lv}
                onClick={() => setActiveLevel(lv)}
                className="text-xs transition-colors"
                style={{ color: activeLevel === lv ? '#DD0000' : '#888', fontWeight: activeLevel === lv ? '700' : '500' }}
              >
                Level {lv}: <strong>{count}</strong>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="text-brand-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            <p className="text-2xl mb-2">🎬</p>
            No videos for Level {activeLevel} yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface border-b border-gray-200">
                <tr>
                  {['Thumbnail', 'Title', 'Category', 'Added', 'Actions'].map(h => (
                    <th
                      key={h}
                      className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((v, i) => (
                  <tr
                    key={v.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 ? 'bg-gray-50/40' : ''}`}
                  >
                    <td className="py-2 px-4">
                      <img
                        src={
                          v.thumbnail_url ||
                          `https://img.youtube.com/vi/${v.youtube_id}/default.jpg`
                        }
                        alt={v.title}
                        className="w-20 h-14 rounded-lg object-cover border border-gray-200"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-de-black text-xs">{v.title}</p>
                      {v.description && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                          {v.description}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-500 capitalize">{v.category}</td>
                    <td className="py-3 px-4 text-xs text-gray-400">
                      {new Date(v.created_at).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <a
                          href={`https://youtu.be/${v.youtube_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="secondary">▶ View</Button>
                        </a>
                        <Button size="sm" variant="danger" onClick={() => setDelete(v)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDelete(null)} title="Delete Video" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-4">
              Delete <strong>"{deleteModal.title}"</strong>?
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDelete(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
